'use client';
import { useState } from 'react';
import { ImageWrapperFragmentFragment } from '@fm-nx/contentful-types';
import AmimationFadeLazy from '../animation-fade-lazy/animation-fade-lazy';
import TextMarkdown from '../text-markdown/text-markdown';
import AnimationImage from '../animation-image/animation-image';
import { useContentfulInspectorMode, useContentfulLiveUpdates } from '@contentful/live-preview/react';
import { CldImage } from 'next-cloudinary';
import classNames from 'classnames';
import { useBreakpoint } from '../hooks/useResponsive';

type ImageWithPartialOverlayProps = {
    id?: string;
    overline: string;
    image: ImageWrapperFragmentFragment;
    headerText: string;
    paragraphText?: string;
    // When true, the image tucks under the preceding hero: its top corners
    // are rounded and a negative margin pulls it up into the hero area.
    // Set by BlockRenderer for the first top-level block.
    tuckUnderHero?: boolean;
};

export const ImageWithPartialOverlay = (props: ImageWithPartialOverlayProps) => {
    const { overline, image, headerText, paragraphText, id, tuckUnderHero } = props;
    const { isTabletBelow, isDesktop } = useBreakpoint();
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const updatedData = useContentfulLiveUpdates(props);
    const inspectorProps = useContentfulInspectorMode({ entryId: id });
    const imageCloudinary = image?.cloudinaryMedia[0];

    if (!image) return null;

    let imageBlock: JSX.Element | null = null;
    if (imageCloudinary) {
        const imageEls = (
            <>
                {isDesktop && (
                    <CldImage
                        {...inspectorProps({
                            fieldId: 'image',
                        })}
                        className="z-0 hidden object-cover md:block"
                        src={imageCloudinary.public_id}
                        alt={image?.altText || ''}
                        priority
                        fill
                        sizes="100vw"
                        onLoad={() => setIsImageLoaded(true)}
                    />
                )}
                {/* mobile/tablet image - cropped to face for people page */}
                {isTabletBelow && (
                    <div className="block image-container-responsive lg:hidden">
                        <CldImage
                            {...inspectorProps({
                                fieldId: 'image',
                            })}
                            className="image-responsive"
                            src={imageCloudinary.public_id}
                            alt={image?.altText || ''}
                            priority
                            crop="thumb"
                            width={1600}
                            height={1600}
                            zoom="0.75"
                            gravity="faces"
                            sizes="100vw"
                            onLoad={() => setIsImageLoaded(true)}
                        />
                    </div>
                )}
            </>
        );

        imageBlock = (
            <div
                className={classNames('relative w-full h-full md:h-[620px]', {
                    'rounded-t-(--radius-fluid-lg) overflow-hidden': tuckUnderHero,
                })}
            >
                {tuckUnderHero ? imageEls : <AnimationImage isImageLoaded={isImageLoaded}>{imageEls}</AnimationImage>}
            </div>
        );
    }

    const blockBody = (
        <>
            {imageBlock}
            <div className="w-full mx-auto max-w-container">
                <div className="relative z-10 mb-14 md:mb-20">
                    <div className="grid grid-cols-1 gap-0 mt-0 md:-mt-10 lg:-mt-36 md:grid-cols-8 lg:grid-cols-2 md:gap-8">
                        <div className="bg-white cols-span-1 md:col-span-6 lg:col-span-1 pt-14">
                            <div className="grid grid-cols-7">
                                <div className="col-span-7 col-start-1 mx-6 md:ml-4 md:mr-0 lg:mx-0 md:col-start-2 md:col-span-5">
                                    <AmimationFadeLazy>
                                        <div className="space-y-6">
                                            <span className="h5 overline">{overline}</span>
                                            <h2
                                                {...inspectorProps({
                                                    fieldId: 'headerText',
                                                })}
                                                className="h-xl"
                                            >
                                                {updatedData?.headerText || ''}
                                            </h2>
                                        </div>
                                    </AmimationFadeLazy>
                                </div>
                            </div>
                        </div>
                        {paragraphText && (
                            <div className="mx-6 md:col-start-2 md:col-span-7 mt-14 md:mx-0 lg:mt-48">
                                <div className="grid grid-cols-7">
                                    <div className="col-span-6">
                                        <AmimationFadeLazy>
                                            <TextMarkdown
                                                {...inspectorProps({
                                                    fieldId: 'paragraphText',
                                                })}
                                                className="prose-intro"
                                                text={updatedData?.paragraphText || ''}
                                            />
                                        </AmimationFadeLazy>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );

    return (
        <div className={classNames('relative', { '-mt-(--radius-fluid-lg)': tuckUnderHero })}>
            {tuckUnderHero ? blockBody : <AmimationFadeLazy>{blockBody}</AmimationFadeLazy>}
        </div>
    );
};

export default ImageWithPartialOverlay;
